import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useState, useEffect, useContext, type ReactElement } from 'react'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import StopGroupList from 'screens/TripDetails/StopsView/StopGroupList'
import StopGroupShipments from 'screens/TripDetails/StopsView/StopGroupShipments'
import { groupStopsBySequence } from 'screens/TripDetails/TripDetails.helpers'
import type { LegData, StopsSequenceGroup } from 'screens/TripDetails/TripDetails.types'

export interface StopsViewProps {
  trip: TripData
  legs: LegData[]
  weblinkToken?: string
}

const StopsView = ({
  trip,
  legs,
  weblinkToken,
}: StopsViewProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [groupedStopsData, setGroupedStopsData] = useState<StopsSequenceGroup[]>([])
  const [activeGroup, setActiveGroup] = useState<StopsSequenceGroup>()
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)

  useEffect(() => {
    const groupedStops = groupStopsBySequence(legs)
    if (!isMobile && !tripDetailsState.activeStopsGroup) {
      // get the first group that's not completed yet or fallback to first overall in case everything's done already
      let firstValidStopsGroup = groupedStops.find((group) => group.state !== 'completed')
        ?? groupedStops.find(() => true)
      tripDetailsDispatch({
        type: 'setActiveStopsGroup',
        payload: firstValidStopsGroup ? firstValidStopsGroup.id : -1,
      })
    }
    setGroupedStopsData(groupedStops)
  }, [legs])

  useEffect(() => {
    setActiveGroup(groupedStopsData.find((group) => group.id === tripDetailsState.activeStopsGroup))
  }, [groupedStopsData, tripDetailsState.activeStopsGroup])

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      sx={{ flexGrow: 1 }}
    >
      <Stack
        sx={{
          position: { lg: 'sticky' },
          maxWidth: { lg: '360px' },
          flex: { lg: '0 0 360px' },
          height: { xs: 'auto', lg: 'calc(100vh - 5.5rem)' },
          top: '4rem',
          minHeight: 0,
        }}
      >
        <StopGroupList
          groups={groupedStopsData}
          trip={trip}
          weblinkToken={weblinkToken}
        />
      </Stack>
      {activeGroup && (
        <StopGroupShipments
          group={activeGroup}
          trip={trip}
          weblinkToken={weblinkToken}
        />
      )}
    </Stack>
  )
}

export default StopsView
